import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface UnitMaster {
  id: string
  organizationId: string
  name: string
  shortCode: string
  displayName: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UnitMasterFilters {
  isActive?: boolean
  search?: string
}

export function useUnitMasters(filters?: UnitMasterFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['unitMasters', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/units?${params}`)
      return (res?.data || []) as UnitMaster[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useUnitMasterMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['unitMasters', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<UnitMaster, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/units', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<UnitMaster> }) =>
      api.patch<any>(`/master-data/units/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/units/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/units/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove }
}